import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  CheckCircle, 
  Mail, 
  ArrowRight,
  Users,
  Clock,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';

const serviceLocations = [
  {
    state: 'Georgia',
    abbreviation: 'GA',
    description: 'Full coverage across the state including Atlanta metro, Augusta, and surrounding areas.',
    services: ['Utility Locating & Marking', 'Damage Prevention', 'Emergency Response', 'Field Intelligence'],
    highlight: 'Headquarters Location'
  },
  {
    state: 'Tennessee',
    abbreviation: 'TN',
    description: 'Comprehensive services throughout Tennessee including Nashville, Memphis, Knoxville, and Chattanooga.',
    services: ['Utility Locating & Marking', 'Damage Prevention', 'Emergency Response', 'Field Intelligence'],
    highlight: 'Growing Region'
  },
  {
    state: 'Illinois',
    abbreviation: 'IL',
    description: 'Serving the greater Chicago area and throughout Illinois with experienced local teams.',
    services: ['Utility Locating & Marking', 'Damage Prevention', 'Emergency Response', 'Field Intelligence'],
    highlight: 'Midwest Hub'
  }
];

const capabilities = [
  {
    icon: Users,
    title: '130+ Experienced Locators',
    description: 'Skilled professionals ready to support utilities, contractors, and municipalities across all service areas.'
  },
  {
    icon: Clock,
    title: '24/7 Emergency Response',
    description: 'Rapid deployment teams available around the clock for storm, outage, and restoration support.'
  },
  {
    icon: Shield,
    title: 'Safety-Driven Operations',
    description: 'Every project begins with a hazard assessment and ends with documented safety compliance.'
  }
];

export default function ServiceAreasPage() {
  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="locations-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Presence</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              OUR SERVICE <span className="neon-text">LOCATIONS</span>
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg">
              USG serves critical infrastructure owners across the Southeast and Midwest. 
              Our teams are strategically positioned in Georgia, Tennessee, and Illinois for rapid response.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Locations Grid */}
      <section className="py-16" data-testid="locations-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {serviceLocations.map((location, index) => (
              <motion.div
                key={location.state}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-skytec-surface rounded-2xl border border-skytec-border p-8 hover:border-skytec-primary/30 transition-all relative overflow-hidden"
                data-testid={`location-${location.abbreviation.toLowerCase()}`}
              >
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-skytec-secondary-dim text-skytec-secondary text-xs font-mono rounded-full">
                    {location.highlight}
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-skytec-primary-dim rounded-xl flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-skytec-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-skytec-fg uppercase">
                      {location.state}
                    </h3>
                    <span className="text-skytec-fg/60 font-mono">{location.abbreviation}</span>
                  </div>
                </div>
                
                <p className="text-skytec-fg/60 mb-6">
                  {location.description}
                </p>
                
                <div className="space-y-2">
                  <h4 className="font-heading text-sm font-bold text-skytec-fg/80 uppercase">Services Available:</h4>
                  {location.services.map((service, i) => (
                    <div key={i} className="flex items-center text-sm">
                      <CheckCircle className="w-4 h-4 text-skytec-accent mr-2" />
                      <span className="text-skytec-fg/70">{service}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Capabilities</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              REGIONAL STRENGTHS
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 bg-skytec-bg rounded-2xl border border-skytec-border"
              >
                <div className="w-16 h-16 bg-skytec-primary-dim rounded-full flex items-center justify-center mx-auto mb-6">
                  <capability.icon className="w-8 h-8 text-skytec-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold text-skytec-fg mb-3 uppercase">
                  {capability.title}
                </h3>
                <p className="text-skytec-fg/60 text-sm">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Visual */}
      <section className="py-24" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              SOUTHEAST & MIDWEST COVERAGE
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto">
              With strategic locations in Georgia, Tennessee, and Illinois, 
              USG is positioned to serve critical infrastructure across the region.
            </p>
          </motion.div>

          {/* Visual representation of states */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex justify-center items-center gap-4 flex-wrap"
          >
            {serviceLocations.map((location, index) => (
              <div 
                key={location.state}
                className="px-12 py-8 bg-skytec-surface rounded-2xl border-2 border-skytec-primary/50 hover:border-skytec-primary transition-colors"
              >
                <div className="text-center">
                  <div className="font-heading text-5xl font-bold text-skytec-primary mb-2">
                    {location.abbreviation}
                  </div>
                  <div className="font-heading text-lg text-skytec-fg">{location.state}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-skytec-surface border-t border-skytec-border" data-testid="locations-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              NEED SERVICE IN YOUR AREA?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Contact us to discuss your project requirements. Our teams in Georgia, 
              Tennessee, and Illinois are ready to support your utility locating needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold"
                  data-testid="locations-contact-btn"
                >
                  REQUEST A QUOTE
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="mailto:info@usgllc811.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  EMAIL US
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
